import React from 'react';

const CheckCircleIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const XCircleIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const LightningIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
  </svg>
);

const JobCostSetupModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  jobCostData, 
  jobCostResults 
}) => {
  if (!isOpen || !jobCostData) return null;

  const { summary, previewXml } = jobCostData;

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-gray-800 rounded-lg shadow-xl w-full max-w-3xl flex flex-col max-h-[90vh] border border-gray-700">
        <div className="p-4 border-b border-gray-700 bg-amber-500/10 rounded-t-lg flex items-center gap-2">
          <LightningIcon className="w-5 h-5 text-amber-400" />
          <h3 className="font-bold text-white">
            {jobCostResults ? 'Job Cost Setup Results' : 'Confirm Job Cost Setup'}
          </h3>
        </div>
        
        <div className="p-6 overflow-y-auto">
          {!jobCostResults ? (
            <>
              <p className="text-gray-300 mb-4">
                Detected <span className="font-bold text-amber-400">Job Cost Setup</span> columns. This will create:
              </p>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="bg-blue-500/10 border border-blue-500/30 rounded p-3">
                  <p className="text-2xl font-bold text-blue-400">{summary.totalTasks}</p>
                  <p className="text-xs text-blue-400 uppercase font-medium">Unique Tasks</p>
                  <p className="text-xs text-gray-500 mt-1">(PROJECTID + TASKID combinations)</p>
                </div>
                <div className="bg-green-500/10 border border-green-500/30 rounded p-3">
                  <p className="text-2xl font-bold text-green-400">{summary.totalCostTypes}</p>
                  <p className="text-xs text-green-400 uppercase font-medium">Unique Cost Types</p>
                  <p className="text-xs text-gray-500 mt-1">(PROJECTID + TASKID + COSTTYPEID)</p>
                </div>
              </div>
              
              <div className="bg-amber-500/10 border border-amber-500/30 rounded p-3 mb-4">
                <p className="text-xs font-bold text-amber-400 mb-2">Process Flow:</p>
                <ol className="text-xs text-amber-300/80 space-y-1 list-decimal ml-4">
                  <li>Create TASKs using STANDARDTASKID (from task library)</li>
                  <li>For any "Standard cost code does not exist" errors, retry with NAME instead</li>
                  <li>Create all COSTTYPEs using STANDARDCOSTTYPEID</li>
                </ol>
              </div>
              
              <div>
                <p className="text-xs font-bold text-gray-400 uppercase mb-1">XML Preview</p>
                <div className="bg-gray-900 p-3 rounded overflow-auto max-h-48 border border-gray-700">
                  <pre className="text-xs text-green-400 font-mono whitespace-pre-wrap">{previewXml}</pre>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Results Summary */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className={`border rounded p-3 ${jobCostResults.summary.tasksFailed === 0 ? 'bg-green-500/10 border-green-500/30' : 'bg-yellow-500/10 border-yellow-500/30'}`}>
                  <p className="text-lg font-bold">
                    <span className="text-green-400">{jobCostResults.summary.tasksSucceeded + jobCostResults.summary.tasksRetriedSucceeded}</span>
                    <span className="text-gray-500"> / </span>
                    <span className="text-gray-400">{jobCostResults.summary.tasksAttempted}</span>
                  </p>
                  <p className="text-xs uppercase font-medium text-gray-400">Tasks Created</p>
                  {jobCostResults.summary.tasksRetried > 0 && (
                    <p className="text-xs text-blue-400 mt-1">
                      {jobCostResults.summary.tasksRetriedSucceeded}/{jobCostResults.summary.tasksRetried} retried successfully
                    </p>
                  )}
                </div>
                <div className={`border rounded p-3 ${jobCostResults.summary.costTypesFailed === 0 ? 'bg-green-500/10 border-green-500/30' : 'bg-yellow-500/10 border-yellow-500/30'}`}>
                  <p className="text-lg font-bold">
                    <span className="text-green-400">{jobCostResults.summary.costTypesSucceeded}</span>
                    <span className="text-gray-500"> / </span>
                    <span className="text-gray-400">{jobCostResults.summary.costTypesAttempted}</span>
                  </p>
                  <p className="text-xs uppercase font-medium text-gray-400">Cost Types Created</p>
                </div>
              </div>
              
              {/* Task Results */}
              {jobCostResults.tasks.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs font-bold text-gray-400 uppercase mb-2">Task Results</p>
                  <div className="border border-gray-600 rounded max-h-40 overflow-y-auto bg-gray-900">
                    {jobCostResults.tasks.map((task, idx) => (
                      <div key={idx} className={`px-3 py-2 text-xs border-b border-gray-700 last:border-b-0 flex items-center gap-2 ${task.status === 'success' ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                        {task.status === 'success' ? (
                          <span className="text-green-400"><CheckCircleIcon /></span>
                        ) : (
                          <span className="text-red-400"><XCircleIcon /></span>
                        )}
                        <span className="font-mono text-gray-300">{task.projectId} / {task.taskId}</span>
                        {task.retried && <span className="text-xs bg-blue-500/20 text-blue-400 px-1 rounded">retried</span>}
                        <span className={`ml-auto truncate max-w-[200px] ${task.status === 'success' ? 'text-green-400' : 'text-red-400'}`} title={task.message}>
                          {task.message}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Cost Type Results */}
              {jobCostResults.costTypes.length > 0 && (
                <div>
                  <p className="text-xs font-bold text-gray-400 uppercase mb-2">Cost Type Results</p>
                  <div className="border border-gray-600 rounded max-h-40 overflow-y-auto bg-gray-900">
                    {jobCostResults.costTypes.map((ct, idx) => (
                      <div key={idx} className={`px-3 py-2 text-xs border-b border-gray-700 last:border-b-0 flex items-center gap-2 ${ct.status === 'success' ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                        {ct.status === 'success' ? (
                          <span className="text-green-400"><CheckCircleIcon /></span>
                        ) : (
                          <span className="text-red-400"><XCircleIcon /></span>
                        )}
                        <span className="font-mono text-gray-300">{ct.projectId} / {ct.taskId} / {ct.costTypeId}</span>
                        <span className={`ml-auto truncate max-w-[200px] ${ct.status === 'success' ? 'text-green-400' : 'text-red-400'}`} title={ct.message}>
                          {ct.message}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        
        <div className="p-4 border-t border-gray-700 bg-gray-900 flex justify-end gap-2 rounded-b-lg">
          <button 
            onClick={onClose} 
            className="px-4 py-2 bg-gray-700 border border-gray-600 rounded text-gray-300 font-medium hover:bg-gray-600"
          >
            {jobCostResults ? 'Close' : 'Cancel'}
          </button>
          {!jobCostResults && (
            <button 
              onClick={onConfirm} 
              className="px-4 py-2 bg-amber-500 hover:bg-amber-400 text-gray-900 rounded font-medium shadow-sm"
            >
              ⚡ Run Job Cost Setup
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default JobCostSetupModal;